<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { resolveTranslatedImageAttachment } from '../mediaTranslation';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();
const showGallery = ref(false);
const imageUrl = ref('');

const {
  filteredCurrentChatAttachments,
  conversationId,
  showOriginalTranslationContent,
} = useMessageContext();

const displayedAttachment = computed(() =>
  resolveTranslatedImageAttachment(props.attachment, {
    showingOriginal: showOriginalTranslationContent.value,
  })
);

const displayedGalleryAttachments = computed(() =>
  filteredCurrentChatAttachments.value.map(item =>
    resolveTranslatedImageAttachment(item, {
      showingOriginal: showOriginalTranslationContent.value,
    })
  )
);

const imageUrlCandidates = computed(() => {
  const {
    dataUrl,
    internalDownloadUrl,
    internal_download_url: internalDownloadUrlSnakeCase,
  } = displayedAttachment.value || {};

  return [dataUrl, internalDownloadUrl, internalDownloadUrlSnakeCase].filter(
    (url, index, urls) => url && urls.indexOf(url) === index
  );
});

const loadImageCandidate = async (index = 0) => {
  const url = imageUrlCandidates.value[index];
  if (!url) return;

  await loadWithRetry(url);
  if (isLoaded.value) {
    imageUrl.value = url;
    return;
  }

  await loadImageCandidate(index + 1);
};

const loadImage = async () => {
  imageUrl.value = '';
  await loadImageCandidate();
};

const galleryAttachment = computed(() => ({
  ...useSnakeCase(displayedAttachment.value),
  data_url: imageUrl.value || displayedAttachment.value?.dataUrl,
}));

onMounted(() => {
  loadImage();
});

watch(imageUrlCandidates, loadImage);
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <div
      v-else-if="!isLoaded"
      class="flex items-center justify-center rounded-lg size-full bg-n-alpha-1"
    >
      <Spinner size="medium" />
    </div>
    <img
      v-else
      class="object-cover w-full h-full skip-context-menu"
      :src="imageUrl"
    />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="galleryAttachment"
    :all-attachments="displayedGalleryAttachments"
    :conversation-id="conversationId"
    prefer-file-name
    @error="hasError = true"
    @close="() => (showGallery = false)"
  />
</template>
